import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BranchCode } from 'api/risk';
import { FormKey } from 'types/form/formKeyEnum';
import { InitSubscriptionProcessData } from '../pricing-process/types/interfaces';
import { ProfilingMode } from '../profiling-process/slices/types/enums';
import { ProfilingConfig } from '../profiling-process/types/interfaces';
import { SubscriptionProcessData, UnemploymentData, UpiData } from '../types/borrowerData';
import { PendingEntity, PendingForm, SimulationConfig, SubscriptionProcessStep } from '../types/interfaces';
import { fetchStepData } from './thunks/fetchStepData';
import { initSubscriptionProcess } from './thunks/initProcessThunk';
import { nextStep } from './thunks/nextStepThunk';

export interface SubscriptionProcessState {
  riskId?: string;
  folderIdentifier?: string;

  //TODO : DEPRECATED
  simulationId?: string;
  simulationTitle?: string;
  simulationNumber?: number;
  dossierId?: string;
  funnelId?: string;

  pendingForm?: PendingForm
  data?: SubscriptionProcessData
  branch?: BranchCode;
  profilingConfig: ProfilingConfig
  simulationConfig?: SimulationConfig,
  steps: SubscriptionProcessStep[]
  loading: {
    initSubscriptionProcess: boolean,
    steps: boolean,
    pendingForm: boolean,
    fetchStepData: boolean,
  }
}

const initialState: SubscriptionProcessState = {
  simulationId: "DEPRECATED",
  simulationTitle: "DEPRECATED",
  simulationNumber: 0,
  dossierId: 'DEPRECATED',
  funnelId: 'DEPRECATED',
  profilingConfig: {
    questionsGroups: [],
    validationRules: [],
    mode: ProfilingMode.AutoAdvance
  },
  loading: {
    initSubscriptionProcess: false,
    steps: false,
    pendingForm: false,
    fetchStepData: false,
  },
  steps: []
};

const subscriptionProcessSlice = createSlice({
  name: 'subscriptionProcess',
  initialState,
  reducers: {
    initSubscriptionProcessData: (state, action: PayloadAction<InitSubscriptionProcessData>) => {
      return { ...state, ...action.payload };
    },
    // Synchronise l'id de risque/projet courant. Indispensable côté emprunteur : plusieurs
    // formulaires (prêt, assuré...) lisent `riskId` comme borrowerProjectId — il DOIT refléter
    // le projet de l'URL, sinon on rattache les données au mauvais projet après un changement
    // de simulation.
    setRiskId: (state, action: PayloadAction<string>) => {
      state.riskId = action.payload;
    },
    setPendingForm: (state, action: PayloadAction<{ formKey?: FormKey, entity: PendingEntity }>) => {
      const { formKey, entity } = action.payload
      state.pendingForm = {
        formKey: formKey || FormKey.Default,
        entity
      }
    },
    resetPendingForm: (state) => {
      state.pendingForm = undefined
    },
    setUPIData: (state, action: PayloadAction<UpiData>) => {
      state.data = action.payload
    },
    setUnemploymentData: (state, action: PayloadAction<UnemploymentData>) => {
      state.data = action.payload
    },
    resetStep: (state) => {
      state.steps = []
    },
    resetSubscriptionProcess: (state) => {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(initSubscriptionProcess.pending, (state) => {
        state.loading.initSubscriptionProcess = true
      })
      .addCase(initSubscriptionProcess.fulfilled, (state, action) => {
        state.loading.initSubscriptionProcess = false;
        if (action.payload) {
          const { step, ...payloadData } = action.payload;
          Object.assign(state, {
            ...payloadData,
            pendingForm: undefined,
            data: undefined,
            steps: [step]
          });
        }
      }).addCase(initSubscriptionProcess.rejected, (state) => {
        state.loading.initSubscriptionProcess = false
      })
      .addCase(nextStep.pending, (state) => {
        state.loading.steps = true;
      })
      .addCase(nextStep.fulfilled, (state, action) => {
        state.loading.steps = false;

        if (state.steps.length > 0) {
          state.steps[state.steps.length - 1].isCurrent = false;
          state.steps[state.steps.length - 1].isValid = true;
        }

        const { data, ...stepWithoutData } = action.payload;

        if (data && action.payload.stepIdentifier) {
          if (!state.data) {
            state.data = {};
          }
          state.data = {
            ...state.data,
            ...data
          };
        }

        state.steps.push(stepWithoutData);
      })
      .addCase(nextStep.rejected, (state) => {
        state.loading.steps = false;
      })
      .addCase(fetchStepData.pending, (state) => {
        state.loading.fetchStepData = true;
      })
      .addCase(fetchStepData.fulfilled, (state, action) => {
        state.loading.fetchStepData = false;
        state.riskId = action.payload.riskId;
        state.data = action.payload.data;
      })
      .addCase(fetchStepData.rejected, (state) => {
        state.loading.fetchStepData = false;
      })
  },
});

export const { initSubscriptionProcessData, setRiskId, setPendingForm, resetPendingForm,
  resetStep, resetSubscriptionProcess, setUPIData, setUnemploymentData } = subscriptionProcessSlice.actions;

export default subscriptionProcessSlice.reducer;
